import React from 'react';

const RustikListPanel = ({
  title,
  icon,
  isCollapsed,
  onToggle,
  placeholder,
  newValue,
  onNewValueChange,
  list,
  editing,
  setEditing,
  onAdd,
  onSaveEdit,
  onDelete
}) => (
  <div className={`rustik-list-panel${isCollapsed ? '' : ' is-open'}`}>
    <button
      type="button"
      className="rustik-list-header"
      onClick={onToggle}
      aria-expanded={!isCollapsed}
    >
      <h5>
        <i className={icon} aria-hidden="true" />
        <span>{title}</span>
        {!isCollapsed && list.length > 0 && (
          <span className="rustik-list-count">{list.length}</span>
        )}
      </h5>
      <i className={`fas fa-chevron-${isCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
    </button>

    {!isCollapsed && (
      <div className="rustik-list-body">
        <div className="list-add-form">
          <input
            type="text"
            placeholder={placeholder}
            value={newValue}
            onChange={(e) => onNewValueChange(e.target.value)}
          />
          <button type="button" className="btn-list-add" onClick={onAdd}>
            <i className="fas fa-plus" aria-hidden="true" />
          </button>
        </div>

        <div className="settings-list-container">
          {list.length === 0 ? (
            <div className="settings-list-empty">Liste boş</div>
          ) : (
            <ul className="settings-list">
              {list.map((item, index) => (
                <li key={index} className="settings-list-item">
                  {editing.index === index ? (
                    <div className="settings-list-edit">
                      <input
                        type="text"
                        value={editing.value}
                        onChange={(e) => setEditing({ ...editing, value: e.target.value })}
                        autoFocus
                      />
                      <button type="button" className="btn-icon btn-icon-success" onClick={() => onSaveEdit(index)}>
                        <i className="fas fa-check" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon btn-icon-danger"
                        onClick={() => setEditing({ index: null, value: '' })}
                      >
                        <i className="fas fa-times" aria-hidden="true" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className="settings-list-label">{item}</span>
                      <div className="settings-list-actions">
                        <button
                          type="button"
                          className="btn-icon"
                          onClick={() => setEditing({ index, value: item })}
                        >
                          <i className="fas fa-edit" aria-hidden="true" />
                        </button>
                        <button type="button" className="btn-icon btn-icon-danger" onClick={() => onDelete(index)}>
                          <i className="fas fa-trash-alt" aria-hidden="true" />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    )}
  </div>
);

const RustikSettings = React.memo(({
  rustikExtras,
  setRustikExtras,
  isRustikSettingsCollapsed,
  setIsRustikSettingsCollapsed,
  isRustikModelCollapsed,
  setIsRustikModelCollapsed,
  isRustikCapCollapsed,
  setIsRustikCapCollapsed,
  isRustikUzunlukCollapsed,
  setIsRustikUzunlukCollapsed,
  isRustikRenkCollapsed,
  setIsRustikRenkCollapsed,
  newRustikModel,
  setNewRustikModel,
  newRustikCap,
  setNewRustikCap,
  newRustikUzunluk,
  setNewRustikUzunluk,
  newRustikRenk,
  setNewRustikRenk,
  editingRustikModel,
  setEditingRustikModel,
  editingRustikCap,
  setEditingRustikCap,
  editingRustikUzunluk,
  setEditingRustikUzunluk,
  editingRustikRenk,
  setEditingRustikRenk,
  handleSaveSettings
}) => {
  const updateList = (key, firestoreKey, newList) => {
    setRustikExtras({ ...rustikExtras, [key]: newList });
    handleSaveSettings({ [firestoreKey]: newList });
  };

  const listPanels = [
    {
      title: 'Ayak Model Listesi',
      icon: 'fas fa-couch',
      isCollapsed: isRustikModelCollapsed,
      onToggle: () => setIsRustikModelCollapsed(!isRustikModelCollapsed),
      placeholder: 'Yeni model',
      newValue: newRustikModel,
      onNewValueChange: setNewRustikModel,
      list: rustikExtras.ayakModelList,
      editing: editingRustikModel,
      setEditing: setEditingRustikModel,
      onAdd: () => {
        if (!newRustikModel) return;
        const newList = [...rustikExtras.ayakModelList, newRustikModel];
        updateList('ayakModelList', 'rustikAyakModelList', newList);
        setNewRustikModel('');
      },
      onSaveEdit: (index) => {
        const newList = [...rustikExtras.ayakModelList];
        newList[index] = editingRustikModel.value;
        updateList('ayakModelList', 'rustikAyakModelList', newList);
        setEditingRustikModel({ index: null, value: '' });
      },
      onDelete: (index) => {
        updateList('ayakModelList', 'rustikAyakModelList', rustikExtras.ayakModelList.filter((_, i) => i !== index));
      }
    },
    {
      title: 'Ayak Çap Listesi',
      icon: 'fas fa-ruler-combined',
      isCollapsed: isRustikCapCollapsed,
      onToggle: () => setIsRustikCapCollapsed(!isRustikCapCollapsed),
      placeholder: 'Yeni çap',
      newValue: newRustikCap,
      onNewValueChange: setNewRustikCap,
      list: rustikExtras.ayakCapList,
      editing: editingRustikCap,
      setEditing: setEditingRustikCap,
      onAdd: () => {
        if (!newRustikCap) return;
        const newList = [...rustikExtras.ayakCapList, newRustikCap];
        updateList('ayakCapList', 'rustikAyakCapList', newList);
        setNewRustikCap('');
      },
      onSaveEdit: (index) => {
        const newList = [...rustikExtras.ayakCapList];
        newList[index] = editingRustikCap.value;
        updateList('ayakCapList', 'rustikAyakCapList', newList);
        setEditingRustikCap({ index: null, value: '' });
      },
      onDelete: (index) => {
        updateList('ayakCapList', 'rustikAyakCapList', rustikExtras.ayakCapList.filter((_, i) => i !== index));
      }
    },
    {
      title: 'Ayak Uzunluğu',
      icon: 'fas fa-arrows-alt-v',
      isCollapsed: isRustikUzunlukCollapsed,
      onToggle: () => setIsRustikUzunlukCollapsed(!isRustikUzunlukCollapsed),
      placeholder: 'Yeni uzunluk',
      newValue: newRustikUzunluk,
      onNewValueChange: setNewRustikUzunluk,
      list: rustikExtras.ayakUzunlukList,
      editing: editingRustikUzunluk,
      setEditing: setEditingRustikUzunluk,
      onAdd: () => {
        if (!newRustikUzunluk) return;
        const newList = [...rustikExtras.ayakUzunlukList, newRustikUzunluk];
        updateList('ayakUzunlukList', 'rustikAyakUzunlukList', newList);
        setNewRustikUzunluk('');
      },
      onSaveEdit: (index) => {
        const newList = [...rustikExtras.ayakUzunlukList];
        newList[index] = editingRustikUzunluk.value;
        updateList('ayakUzunlukList', 'rustikAyakUzunlukList', newList);
        setEditingRustikUzunluk({ index: null, value: '' });
      },
      onDelete: (index) => {
        updateList('ayakUzunlukList', 'rustikAyakUzunlukList', rustikExtras.ayakUzunlukList.filter((_, i) => i !== index));
      }
    },
    {
      title: 'Renk Kodu Listesi',
      icon: 'fas fa-palette',
      isCollapsed: isRustikRenkCollapsed,
      onToggle: () => setIsRustikRenkCollapsed(!isRustikRenkCollapsed),
      placeholder: 'Yeni renk kodu',
      newValue: newRustikRenk,
      onNewValueChange: setNewRustikRenk,
      list: rustikExtras.ayakRenkKoduList,
      editing: editingRustikRenk,
      setEditing: setEditingRustikRenk,
      onAdd: () => {
        if (!newRustikRenk) return;
        const newList = [...rustikExtras.ayakRenkKoduList, newRustikRenk];
        updateList('ayakRenkKoduList', 'rustikAyakRenkKoduList', newList);
        setNewRustikRenk('');
      },
      onSaveEdit: (index) => {
        const newList = [...rustikExtras.ayakRenkKoduList];
        newList[index] = editingRustikRenk.value;
        updateList('ayakRenkKoduList', 'rustikAyakRenkKoduList', newList);
        setEditingRustikRenk({ index: null, value: '' });
      },
      onDelete: (index) => {
        updateList('ayakRenkKoduList', 'rustikAyakRenkKoduList', rustikExtras.ayakRenkKoduList.filter((_, i) => i !== index));
      }
    }
  ];

  return (
    <div className="settings-card rustik-settings-card">
      <div
        className="settings-header clickable"
        onClick={() => setIsRustikSettingsCollapsed(!isRustikSettingsCollapsed)}
      >
        <div className="settings-title">
          <i className="fas fa-shoe-prints" aria-hidden="true" />
          <span>Rustik Ayak Ücret Ayarları</span>
        </div>
        <i className={`fas fa-chevron-${isRustikSettingsCollapsed ? 'down' : 'up'}`} aria-hidden="true" />
      </div>

      {!isRustikSettingsCollapsed && (
        <div className="settings-content settings-content-padded">
          <div className="rustik-price-card">
            <div className="rustik-price-card-info">
              <h4>Varsayılan Ayak Ücreti</h4>
              <p>Rustik siparişlerinde kullanılacak standart ayak birim fiyatı. Sipariş formuna otomatik yansır.</p>
            </div>
            <div className="form-group rustik-price-input">
              <label htmlFor="rustik-ayak-price">Birim Fiyat (₺)</label>
              <input
                id="rustik-ayak-price"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={rustikExtras.ayakPrice || ''}
                onChange={(e) => setRustikExtras({ ...rustikExtras, ayakPrice: e.target.value })}
              />
            </div>
          </div>

          <div className="rustik-lists-section">
            <h5 className="rustik-lists-section-title">
              <i className="fas fa-sliders-h" aria-hidden="true" />
              Ayak Ek Ayarları
            </h5>
            <div className="rustik-lists-grid">
              {listPanels.map((panel) => (
                <RustikListPanel key={panel.title} {...panel} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default RustikSettings;
